import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Copy, Info, KeyRound, Loader2, Monitor, Plus, RefreshCw } from 'lucide-react';
import { Button } from '../Button';
import type { PlotAgent, PlotAgentNeu, PlotAnlageStatus, PlotErreichbarkeitStufe } from '../../services/plotter/plotTypen';
import { erreichbarkeit, relativeZeit, uhrVersatzMs } from '../../services/plotter/plotStatus';
import { plotAgentAnlegen, plotAgentSperren, plotAnlageStatusLaden } from '../../services/plotter/plotAuftraege';

/**
 * Abschnitt „SiGe-Plan: Druck-PC für den Plotter“ der globalen Einstellungen.
 *
 * Ein immer laufender Büro-PC holt die Plot-Aufträge ab (tools/plotter-agent).
 * Hier wird er angemeldet: der Schlüssel erscheint genau einmal — gespeichert
 * ist nur sein Hash. Sperren fragt inline nach, kein Browser-Dialog.
 * Geladen wird erst beim Aufklappen.
 */

const NAME_MAX = 80;
const INPUT = 'w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-teal-500';

const fehlerText = (e: unknown): string => (e instanceof Error ? e.message : String(e));

const STUFE: Record<PlotErreichbarkeitStufe, { farbe: string; text: string }> = {
    erreichbar: { farbe: 'bg-emerald-500', text: 'erreichbar' },
    verzoegert: { farbe: 'bg-amber-500', text: 'verzögert' },
    nie_verbunden: { farbe: 'bg-amber-500', text: 'hat sich noch nie gemeldet' },
    kein_agent: { farbe: 'bg-slate-300', text: 'gesperrt' },
    nicht_erreichbar: { farbe: 'bg-red-500', text: 'nicht erreichbar' },
    nicht_eingerichtet: { farbe: 'bg-slate-300', text: 'nicht eingerichtet' },
};

/** Bekannte Angaben des Agenten zuerst und mit deutscher Bezeichnung, der Rest wie geliefert. */
const INFO_BEZEICHNUNG: Record<string, string> = {
    rechner: 'Rechner', rechnername: 'Rechner', computer: 'Rechner', hostname: 'Rechner',
    drucker: 'Drucker', printer: 'Drucker',
    version: 'Version',
};

const infoZeile = (info: Record<string, unknown> | null): string => {
    if (!info) return '';
    const teile: string[] = [];
    for (const [schluessel, wert] of Object.entries(info)) {
        if (wert === null || wert === undefined || wert === '') continue;
        if (typeof wert !== 'string' && typeof wert !== 'number' && typeof wert !== 'boolean') continue;
        teile.push(`${INFO_BEZEICHNUNG[schluessel.toLowerCase()] ?? schluessel}: ${String(wert)}`);
    }
    return teile.join(' · ');
};

/**
 * Erreichbarkeit EINES Agenten — dieselben Schwellen wie für die ganze Firma.
 * Die Kopie kennt die Empfangszeit der Antwort nicht (plotStatus.ts); daher
 * kommt die Uhr hier schon auf Serverzeit gezogen herein.
 */
const agentErreichbarkeit = (status: PlotAnlageStatus, agent: PlotAgent, serverMs: number) =>
    erreichbarkeit({ ...status, agenten: [agent] }, serverMs);

export const PlotterAgentenVerwaltung: React.FC = () => {
    const [offen, setOffen] = useState(false);
    // undefined = noch nicht geladen; null = SQL Nr. 157/158 fehlt.
    const [status, setStatus] = useState<PlotAnlageStatus | null | undefined>(undefined);
    const [laedt, setLaedt] = useState(false);
    const [ladeFehler, setLadeFehler] = useState('');
    const [jetztMs, setJetztMs] = useState(0);

    const [name, setName] = useState('');
    const [legtAn, setLegtAn] = useState(false);
    const [anlegenFehler, setAnlegenFehler] = useState('');
    const [neu, setNeu] = useState<PlotAgentNeu | null>(null);
    const [kopiert, setKopiert] = useState<'ja' | 'von_hand' | null>(null);

    const [sperrenFrage, setSperrenFrage] = useState<string | null>(null);
    const [sperrt, setSperrt] = useState<string | null>(null);
    const [sperrenFehler, setSperrenFehler] = useState<{ id: string; text: string } | null>(null);

    const aktivRef = useRef(true);
    const laedtRef = useRef(false);
    const nochmalRef = useRef(false);
    // Zählt abgeschlossene Änderungen (Anlegen, Sperren): eine Antwort, deren
    // Laden vorher begann, zeigt den alten Stand und wird verworfen.
    const standRef = useRef(0);
    const legtAnRef = useRef(false);
    const sperrtRef = useRef(false);
    const schluesselRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        aktivRef.current = true;
        return () => { aktivRef.current = false; };
    }, []);

    const laden = async () => {
        // Kein zweiter Ladevorgang daneben; ein Wunsch währenddessen wird danach erfüllt.
        if (laedtRef.current) { nochmalRef.current = true; return; }
        laedtRef.current = true;
        setLaedt(true);
        setLadeFehler('');
        try {
            do {
                nochmalRef.current = false;
                const stand = standRef.current;
                let s: PlotAnlageStatus | null = null;
                let fehler = '';
                try {
                    s = await plotAnlageStatusLaden();
                } catch (e) {
                    fehler = fehlerText(e);
                }
                if (!aktivRef.current) return;
                if (stand !== standRef.current) {
                    nochmalRef.current = true;
                } else if (fehler) {
                    setLadeFehler(fehler);
                } else {
                    setStatus(s);
                    setJetztMs(Date.now());
                    setLadeFehler('');
                }
            } while (nochmalRef.current && aktivRef.current);
        } finally {
            laedtRef.current = false;
            if (aktivRef.current) setLaedt(false);
        }
    };

    /** Nach einer Änderung: ältere Antworten verwerfen, frisch laden. */
    const nachAenderungLaden = () => {
        standRef.current += 1;
        void laden();
    };

    const umschalten = () => {
        const wirdOffen = !offen;
        setOffen(wirdOffen);
        if (wirdOffen && status === undefined) void laden();
    };

    const nameSauber = name.trim();
    const nameGueltig = nameSauber.length >= 1 && nameSauber.length <= NAME_MAX;

    const anlegen = async () => {
        if (legtAnRef.current || !nameGueltig) return;
        legtAnRef.current = true;
        setLegtAn(true);
        setAnlegenFehler('');
        try {
            const agent = await plotAgentAnlegen(nameSauber);
            if (!aktivRef.current) return;
            setNeu(agent);
            setKopiert(null);
            setName('');
            nachAenderungLaden();
        } catch (e) {
            if (aktivRef.current) setAnlegenFehler(fehlerText(e));
        } finally {
            legtAnRef.current = false;
            if (aktivRef.current) setLegtAn(false);
        }
    };

    const kopieren = async (token: string) => {
        try {
            await navigator.clipboard.writeText(token);
            if (aktivRef.current) setKopiert('ja');
            return;
        } catch {
            // Ohne Zwischenablage-Recht (http, alte Browser): über die Markierung.
        }
        if (!aktivRef.current) return;
        const feld = schluesselRef.current;
        let ok = false;
        if (feld) {
            feld.focus();
            feld.select();
            try { ok = document.execCommand('copy'); } catch { ok = false; }
        }
        setKopiert(ok ? 'ja' : 'von_hand');
    };

    const sperren = async (id: string) => {
        if (sperrtRef.current) return;
        sperrtRef.current = true;
        setSperrt(id);
        setSperrenFehler(null);
        try {
            await plotAgentSperren(id);
            if (!aktivRef.current) return;
            setSperrenFrage(null);
            nachAenderungLaden();
        } catch (e) {
            if (aktivRef.current) setSperrenFehler({ id, text: fehlerText(e) });
        } finally {
            sperrtRef.current = false;
            if (aktivRef.current) setSperrt(null);
        }
    };

    const agenten = status?.agenten ?? [];
    const gesamt = status ? erreichbarkeit(status, jetztMs) : null;
    // letzter_kontakt ist Serverzeit.
    const serverMs = jetztMs + uhrVersatzMs(status ?? null);
    const kopfZeile = status === undefined
        ? 'Ein Büro-PC holt die Plot-Aufträge ab und druckt sie über den HP-Treiber'
        : status === null
            ? 'Noch nicht eingerichtet'
            : `${agenten.filter(a => !a.gesperrt).length} aktiv · ${gesamt?.text ?? ''}`;

    return (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="p-6 flex justify-between items-center cursor-pointer hover:bg-slate-50 transition-colors" onClick={umschalten}>
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-teal-50 text-teal-600 rounded-lg"><Monitor className="w-5 h-5" /></div>
                    <div>
                        <h4 className="text-base font-bold text-slate-800">SiGe-Plan: Druck-PC für den Plotter</h4>
                        <p className="text-xs text-slate-500">{kopfZeile}</p>
                    </div>
                </div>
                <div className="text-slate-400">{offen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}</div>
            </div>
            {offen && (
                <div className="p-6 pt-0 border-t border-slate-100 bg-white">
                    <div className="space-y-5 mt-4">
                        <div className="flex items-start gap-3 bg-teal-50 p-3 rounded-xl border border-teal-100">
                            <Info className="w-4 h-4 text-teal-600 shrink-0 mt-0.5" />
                            <p className="text-xs text-teal-900 leading-relaxed">
                                Ein immer laufender Büro-PC mit installiertem HP-Treiber holt die Aufträge ab, die über „An Plotter senden“
                                in der Plan-Vorschau eingehen, und gibt sie an den Plotter. Er braucht nur Internet, keine Portfreigabe.
                                Einrichtung siehe Anleitung im Ordner <code className="font-mono">tools/plotter-agent</code> (README).
                            </p>
                        </div>

                        {ladeFehler && (
                            <div className="flex flex-wrap items-center gap-3">
                                <p className="text-xs text-red-700 flex-1 min-w-[12rem]">Status nicht geladen: {ladeFehler}</p>
                                <Button variant="secondary" size="sm" onClick={() => { void laden(); }} disabled={laedt}>Erneut versuchen</Button>
                            </div>
                        )}

                        {status === undefined && !ladeFehler && (
                            <p className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Lädt …</p>
                        )}

                        {status === null && (
                            <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-xl p-3">
                                Noch nicht eingerichtet — SQL Nr. 157/158 fehlt in dieser Umgebung.
                            </p>
                        )}

                        {status && (
                            <>
                                <div>
                                    <div className="flex items-center justify-between gap-2 mb-1">
                                        <label className="block text-[11px] font-bold text-slate-400 uppercase">Angemeldete Druck-PCs</label>
                                        <button
                                            type="button"
                                            onClick={() => { void laden(); }}
                                            disabled={laedt}
                                            aria-label="Aktualisieren"
                                            title="Aktualisieren"
                                            className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-40"
                                        >
                                            <RefreshCw className={`w-4 h-4 ${laedt ? 'animate-spin' : ''}`} />
                                        </button>
                                    </div>
                                    {agenten.length === 0 ? (
                                        <p className="text-xs text-slate-400">Noch kein Druck-PC angemeldet.</p>
                                    ) : (
                                        <ul className="divide-y divide-slate-100 border border-slate-200 rounded-xl">
                                            {agenten.map(agent => {
                                                const stufe = STUFE[agentErreichbarkeit(status, agent, serverMs).stufe];
                                                const info = infoZeile(agent.info);
                                                const frage = sperrenFrage === agent.id;
                                                return (
                                                    <li key={agent.id} className={`p-3 ${agent.gesperrt ? 'bg-slate-50' : ''}`}>
                                                        <div className="flex flex-wrap items-start justify-between gap-2">
                                                            <div className="min-w-0">
                                                                <p className={`text-sm font-semibold ${agent.gesperrt ? 'text-slate-400 line-through' : 'text-slate-800'}`}>{agent.name}</p>
                                                                {agent.gesperrt ? (
                                                                    <p className="text-xs text-slate-400">gesperrt</p>
                                                                ) : (
                                                                    <p className="flex items-center gap-1.5 text-xs text-slate-600">
                                                                        <span className={`w-2 h-2 rounded-full shrink-0 ${stufe.farbe}`} aria-hidden />
                                                                        {stufe.text}
                                                                        {agent.letzter_kontakt && <span className="text-slate-400"> · letzter Kontakt {relativeZeit(agent.letzter_kontakt, serverMs)}</span>}
                                                                    </p>
                                                                )}
                                                                {info && <p className="text-[11px] text-slate-400 mt-0.5 break-words">{info}</p>}
                                                            </div>
                                                            {!agent.gesperrt && !frage && (
                                                                <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50 hover:text-red-700"
                                                                    onClick={() => { setSperrenFehler(null); setSperrenFrage(agent.id); }}>
                                                                    Sperren
                                                                </Button>
                                                            )}
                                                        </div>
                                                        {frage && (
                                                            <div role="alert" className="mt-2 flex flex-wrap items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-3">
                                                                <p className="text-xs text-red-800 flex-1 min-w-[12rem]">
                                                                    „{agent.name}“ sperren? Er holt danach keine Aufträge mehr ab. Für einen neuen Schlüssel den PC neu anmelden.
                                                                </p>
                                                                <Button variant="ghost" size="sm" onClick={() => setSperrenFrage(null)} disabled={sperrt === agent.id}>Abbrechen</Button>
                                                                <Button variant="danger" size="sm" onClick={() => { void sperren(agent.id); }} loading={sperrt === agent.id}>Sperren</Button>
                                                            </div>
                                                        )}
                                                        {sperrenFehler?.id === agent.id && <p className="text-xs text-red-700 mt-2">{sperrenFehler.text}</p>}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </div>

                                {neu && (
                                    <div className="space-y-2 bg-amber-50 border border-amber-200 rounded-xl p-4">
                                        <p className="flex items-center gap-2 text-sm font-bold text-amber-900">
                                            <KeyRound className="w-4 h-4" /> Schlüssel für „{neu.name}“
                                        </p>
                                        <div className="flex flex-wrap sm:flex-nowrap items-center gap-2">
                                            <input
                                                ref={schluesselRef}
                                                readOnly
                                                value={neu.token}
                                                onFocus={(e) => e.currentTarget.select()}
                                                aria-label="Schlüssel des Druck-PCs"
                                                className={`${INPUT} font-mono text-xs bg-white flex-1 min-w-0`}
                                            />
                                            <Button variant="secondary" size="md" onClick={() => { void kopieren(neu.token); }}
                                                icon={<Copy className="w-4 h-4" />}>
                                                {kopiert === 'ja' ? 'Kopiert ✓' : 'Kopieren'}
                                            </Button>
                                        </div>
                                        {kopiert === 'von_hand' && (
                                            <p className="text-xs text-amber-900">Kopieren ging nicht — bitte den Schlüssel markieren und mit Strg+C kopieren.</p>
                                        )}
                                        <p className="text-xs text-amber-900 leading-relaxed">
                                            In <code className="font-mono">config.json</code> beim Feld <code className="font-mono">agentToken</code> eintragen. Der Schlüssel wird nur jetzt angezeigt.
                                        </p>
                                        <div className="flex justify-end">
                                            <Button variant="ghost" size="sm" onClick={() => setNeu(null)}>Fertig, ausblenden</Button>
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <label htmlFor="plotter-agent-name" className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Neuen Druck-PC anmelden</label>
                                    <div className="flex flex-wrap sm:flex-nowrap items-center gap-2">
                                        <input
                                            id="plotter-agent-name"
                                            type="text"
                                            value={name}
                                            maxLength={NAME_MAX}
                                            placeholder="Name, z. B. Büro Plotter-PC"
                                            onChange={(e) => { setName(e.target.value); setAnlegenFehler(''); }}
                                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void anlegen(); } }}
                                            className={`${INPUT} flex-1 min-w-[12rem]`}
                                        />
                                        <Button variant="primary" size="md" onClick={() => { void anlegen(); }} disabled={!nameGueltig}
                                            loading={legtAn} icon={<Plus className="w-4 h-4" />}>
                                            Anmelden
                                        </Button>
                                    </div>
                                    {anlegenFehler && <p className="text-xs text-red-700 mt-1">{anlegenFehler}</p>}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
